import { useState, useContext, useEffect, useRef } from "react";
import {  FaGlobe,  FaPlusCircle, FaFileInvoiceDollar, FaInfoCircle, FaExclamationTriangle, FaCheckCircle, FaUpload } from "react-icons/fa";
import Button from "components/ui/controls/button/button";
import { createStyledButton } from "components/ui/controls/button/button-styles";
import CardBox from "components/ui/controls/card-box/card-box";
import Column from "components/ui/layout/column/column";
import Row from "components/ui/layout/row/row";
import Modal from "components/ui/modal/modal";
import { AppContext } from "components/app/RemExprertProvider";
import { applyFormat } from "components/ui/ui-helpers";
import ContexteFiscalPageModal from "./contexte fiscal pager";
import { RemExpertResultatTNS } from "components/app/Api/ResultatRemExpertTNSProvider";
import { RemExpertResultatSalarie } from "components/app/Api/ResultatRemExpertSalarieProvider";
import "components/app/AuditComplet/ContexteFiscal/ContexteFiscal.css";
import { CustomTooltip } from "components/ui/controls/custom-tooltip/custom-tooltip";
import { GetInfosFromAvisImpot } from "components/app/Api/Remuneration/ApiRemuneration";
import TextField from "components/ui/controls/text-field/text-field";
import Dialogs from "components/ui/dialogs/dialogs";
import Pager from "components/ui/controls/pager/pager";
import InfoMessage from "components/ui/controls/info-message/info-message";
import { AuthContext } from "components/auth/auth-provider";

const ContexteFiscalePage = (props) => {
  const context = useContext(AppContext);
  const [modalOpen, setmodalOpen] = useState(false);
  const ResultatTNS = useContext(RemExpertResultatTNS);
  const ResultatSalarie = useContext(RemExpertResultatSalarie);
  const authContext = useContext(AuthContext);
  const hasPackExpert = authContext?.utilisateur_reel?.AccesMakoFormation === true;
  const [modalPackExpert, setModalPackExpert] = useState(false);
  const [isUploadingIR, setIsUploadingIR] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [avisImpotData, setAvisImpotData] = useState(null);
  const [modalAvisImpotOpen, setModalAvisImpotOpen] = useState(false);
  const declarationIrInputRef = useRef(null);

  useEffect(() => {
    if (!isUploadingIR) {
      setUploadProgress(0);
      return;
    }
    setUploadProgress(0);
    const intervals = [
      { target: 30, delay: 80 },
      { target: 55, delay: 150 },
      { target: 70, delay: 250 },
      { target: 80, delay: 400 },
      { target: 88, delay: 700 },
      { target: 93, delay: 1200 },
    ];
    let current = 0;
    let timeouts = [];
    let elapsed = 0;
    intervals.forEach(({ target, delay }) => {
      const steps = target - current;
      for (let i = 1; i <= steps; i++) {
        const val = current + i;
        elapsed += delay;
        timeouts.push(setTimeout(() => setUploadProgress(val), elapsed));
      }
      current = target;
    });
    return () => timeouts.forEach(clearTimeout);
  }, [isUploadingIR]);

  const handleDeclarationIrUploadClick = () => {
    if (!hasPackExpert) {
      setModalPackExpert(true);
      return;
    }
    declarationIrInputRef.current?.click();
  };

  const handleDeclarationIrFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setIsUploadingIR(true);
      const response = await GetInfosFromAvisImpot(file, context.state);
      const getTraitement = (declarant) => {
        if (!declarant) return 0;
        const PeriodeClient = context.state["PeriodeActuelle"][0];
        if (PeriodeClient?.Statut === "TNS Article 62") return declarant.salaires || 0;
        if (PeriodeClient?.Statut === "Assimilé salarié") return declarant.revenus_associes_gerants || 0;
        return declarant.salaires || 0;
      };
      setAvisImpotData({
        annee: response.annee_impot || "",
        creditImpot: Math.abs(response.credit_impot || 0),
        deductionFiscale: response.deductions_fiscales?.total || 0,
        declarant1: {
          nom: response.declarant_1?.nom || "Déclarant 1",
          traitement: getTraitement(response.declarant_1),
          autresRevenus: response.declarant_1?.autres_revenus_imposables || 0,
          revenus_pro_type: response.declarant_1?.revenus_pro_type || null,
          revenus_pro_regime_special_declares: response.declarant_1?.revenus_pro_regime_special_declares || 0,
          revenus_pro_abattement: response.declarant_1?.revenus_pro_abattement || 0,
          revenus_pro_regime_special_nets: response.declarant_1?.revenus_pro_regime_special_nets || 0,
        },
        declarant2: {
          nom: response.declarant_2?.nom || "Déclarant 2",
          traitement: (response.declarant_2?.salaires || 0) + (response.declarant_2?.revenus_associes_gerants || 0),
          autresRevenus: response.declarant_2?.autres_revenus_imposables || 0,
          revenus_pro_type: response.declarant_2?.revenus_pro_type || null,
          revenus_pro_regime_special_declares: response.declarant_2?.revenus_pro_regime_special_declares || 0,
          revenus_pro_abattement: response.declarant_2?.revenus_pro_abattement || 0,
          revenus_pro_regime_special_nets: response.declarant_2?.revenus_pro_regime_special_nets || 0,
        },
      });
      setModalAvisImpotOpen(true);
    } catch (error) {
      Dialogs.alert("Erreur lors de l'import de la déclaration IR.", "Erreur");
    } finally {
      setIsUploadingIR(false);
      event.target.value = "";
    }
  };

  const handleConfirmAvisImpot = () => {
    if (!avisImpotData) return;
    context.handleValueChange("ReductionImpot", Number(avisImpotData.creditImpot));
    context.handleValueChange("DeductionFiscale", Number(avisImpotData.deductionFiscale));
    context.handleValueChange("IRDirigeantTraitement", Number(avisImpotData.declarant1.traitement));
    context.handleValueChange("IRConjointTraitement", Number(avisImpotData.declarant2.traitement));
    context.handleValueChange("IRDirigeantAutresRevenus", Number(avisImpotData.declarant1.autresRevenus));
    context.handleValueChange("IRConjointAutresRevenus", Number(avisImpotData.declarant2.autresRevenus));
    const PeriodeClient = context.state["PeriodeActuelle"][0];
    if (PeriodeClient?.Statut !== "TNS individuel") {
      const processRevenusPro = (declarant, prefix) => {
        const declares = declarant.revenus_pro_regime_special_declares;
        const nets = declarant.revenus_pro_regime_special_nets;
        const type = declarant.revenus_pro_type;
        if (declares && nets && declares > 0) {
          const ratio = Math.round((nets / declares) * 100) / 100;
          let fieldName = null;
          if (Math.abs(ratio - 0.66) < 0.02) fieldName = `${prefix}BNCMicro`;
          else if (Math.abs(ratio - 0.29) < 0.02) fieldName = `${prefix}BICMicroAchatRevente`;
          else if (Math.abs(ratio - 0.5) < 0.02) fieldName = `${prefix}BICMicroService`;
          else if (Math.abs(ratio - 0.13) < 0.02) fieldName = `${prefix}BAMicro`;
          else {
            if (type === "BNC") fieldName = `${prefix}BNCControlle`;
            else if (type === "BIC") fieldName = `${prefix}BICReel`;
            else if (type === "BA") fieldName = `${prefix}BAReel`;
          }
          if (fieldName) context.handleValueChange(fieldName, Number(nets));
        }
      };
      processRevenusPro(avisImpotData.declarant1, "IRDirigeant");
      processRevenusPro(avisImpotData.declarant2, "IRConjoint");
    }
    setModalAvisImpotOpen(false);
    
  };

  useEffect(() => {
    const PeriodeClient = context.state["PeriodeActuelle"][0];
    const PeriodeConjoint = context.state["PeriodeActuelleConjoint"][0];

    let BrutImposableClient = 0;
    let BrutImposableConjoint = 0;
    if (PeriodeClient?.Statut === "TNS Article 62" || PeriodeClient?.Statut === "TNS individuel") {
      BrutImposableClient = Number(ResultatTNS.state.RemunerationTNS.BrutImposable);
    } else {
      BrutImposableClient = Number(ResultatSalarie.state["RemunerationSalarie"].BrutImposable);
    }

    if (PeriodeConjoint?.Statut === "TNS Article 62" || PeriodeConjoint?.Statut === "TNS individuel") {
      BrutImposableConjoint = Number(ResultatTNS.state.RemunerationTNSConjoint.BrutImposable);
    } else {
      BrutImposableConjoint = Number(ResultatSalarie.state.RemunerationSalarieConjoint.BrutImposable);
      console.log("2",BrutImposableConjoint)
    }

    if (context.state.InclureConjoint === true && PeriodeClient?.StatutConjoint !== "Aucun") {
      if (PeriodeClient?.StatutConjoint === "TNS Article 62" || PeriodeClient?.StatutConjoint === "TNS individuel") {
        console.log("3")
        BrutImposableConjoint = Number(ResultatTNS.state.RemunerationTNSConjoint.BrutImposable);
      } else {
        console.log("4",BrutImposableConjoint)
        BrutImposableConjoint = Number(ResultatSalarie.state.RemunerationSalarieConjoint.BrutImposable);
      }
    }
    if (PeriodeClient !== undefined) {
      let BrutImposableClient = 0;
      let BrutImposableConjoint = 0;
      if (PeriodeClient?.Statut === "TNS Article 62" || PeriodeClient?.Statut === "TNS individuel") {
        BrutImposableClient = Number(ResultatTNS.state.RemunerationTNS.BrutImposable);
      } else {
        BrutImposableClient = Number(ResultatSalarie.state["RemunerationSalarie"].BrutImposable);
      }

      if (PeriodeConjoint?.Statut === "TNS Article 62" || PeriodeConjoint?.Statut === "TNS individuel") {
        BrutImposableConjoint = Number(ResultatTNS.state.RemunerationTNSConjoint.BrutImposable);
        console.log("5",BrutImposableConjoint)
      } else {
        BrutImposableConjoint = Number(ResultatSalarie.state.RemunerationSalarieConjoint.BrutImposable);
        console.log("6",BrutImposableConjoint)
      }


      if (context.state.InclureConjoint === true && PeriodeClient?.StatutConjoint !== "Aucun") {
        if (PeriodeClient?.StatutConjoint === "TNS Article 62" || PeriodeClient?.StatutConjoint === "TNS individuel") {
          BrutImposableConjoint = Number(ResultatTNS.state.RemunerationTNSConjoint.BrutImposable);
        } else {
          BrutImposableConjoint = Number(ResultatSalarie.state.RemunerationSalarieConjoint.BrutImposable);
        }
      }
      if (PeriodeClient !== undefined) {
        const DividendeClient = PeriodeClient && PeriodeClient.DividendeBrut ? PeriodeClient.DividendeBrut : 0;
        const DividendeConjoint = PeriodeConjoint && PeriodeConjoint.DividendeBrut ? PeriodeConjoint.DividendeBrut : 0;
        ResultatTNS.GetImpot(props.Outil, context.state, BrutImposableClient, BrutImposableConjoint, Number(DividendeClient) + Number(DividendeConjoint));
      }
    }
  }, [
    ResultatTNS.state["RemunerationTNS"].BrutImposable,
    ResultatTNS.state.RemunerationTNSConjoint.BrutImposable,
    ResultatSalarie.state["RemunerationSalarie"].BrutImposable,
    ResultatSalarie.state.RemunerationSalarieConjoint.BrutImposable,
    modalOpen,
    modalAvisImpotOpen,
  ]);

  return (
    <>
      <Column fill>
        <Row>
          <Column fill>
            <CardBox>
              <Column fill>
                <Row>
                  <div className="dossier-header">
                    <div className="dossier-header-icon">
                      <FaFileInvoiceDollar />
                    </div>
                    <h1 className="dossier-header-title">Contexte fiscal</h1>
                  </div>
                </Row> 
                <Row center>
                  <Column style={{paddingLeft:0}}>
                    <CustomTooltip 
                      content={`
                                      ✨  Ajouter d'autres revenus du foyer fiscal pour enrichir votre analyse.
                                    `}
                      type="info"
                    >
                      <Button
                        {...createStyledButton("primary", "primary")}
                        onClick={() => {
                          setmodalOpen(true);
                        }}
                      >
                        <FaPlusCircle style={{ marginRight: "8px" }} />
                        Ajouter un contexte fiscal
                      </Button>
                    </CustomTooltip>
                  </Column>
                  <Column style={{paddingLeft:8}}>
                    <CustomTooltip
                      content="✨  Importer votre avis d'imposition pour pré-remplir automatiquement les données fiscales."
                      type="info"
                    >
                      <Button
                        {...createStyledButton("secondary", "green")}
                        onClick={handleDeclarationIrUploadClick}
                        disabled={isUploadingIR}
                      >
                        <FaUpload style={{ marginRight: "8px" }} />
                        Importer l'avis d'impôt
                      </Button>
                    </CustomTooltip>
                  </Column>
                </Row>
                <Row tpad>
                  <div className="revenuspro-container" style={{ margin: 0, padding: 0 }}>
                    {/* Header du tableau */}
                    <div className="header-card">
                      <div className="category-column">
                        <span className="header-icon">🏷️</span>
                        Type d'impôt
                      </div>
                      <div className="value-column">
                        <span className="header-icon">📊</span>
                        Assiette
                      </div>
                      <div className="value-column">
                        <span className="header-icon">💰</span>
                        Montant à payer
                      </div>
                    </div>

                    {/* Conteneur des cartes de données fiscales */}
                    <div className="card-list-container">
                      {ResultatTNS.state.DetailImpot.BaseImposable > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">📈</div>
                            Impôt lié au barème progressif
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.BaseImposable, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.IRapresQF, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                        </div>
                      )}

                      {ResultatTNS.state.DetailImpot.PFU > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">🏦</div>
                            PFU
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.AssietteTotaleIR.RevenuSoumisPFU, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.PFU, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                        </div>
                      )}

                      {ResultatTNS.state.DetailImpot.ContribHR > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">🏛️</div>
                            CEHR
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.AssietteTotaleIR?.CEHR, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.ContribHR, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                        </div>
                      )}

                      {ResultatTNS.state.DetailImpot.PensionCapital > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">🏛️</div>
                            Pensions de retraite en capital taxables à 7,5%
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.AssietteImpot.Foyer.PensionCapital, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.PensionCapital, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                        </div>
                      )}

                      {Number(ResultatTNS.state.DetailImpot.ReductionImpot) + Number(ResultatTNS.state.DetailImpot.ReductionVeuf) > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">💸</div>
                            Réductions et crédits d'impôt
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            -
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            - {applyFormat(Number(ResultatTNS.state.DetailImpot.ReductionImpot) + Number(ResultatTNS.state.DetailImpot.ReductionVeuf), "uint", {
                              thousands: " ",
                              decimal: ",",
                              unit: " €",
                            })}
                          </div>
                        </div>
                      )}

                      {ResultatTNS.state.DetailImpot.Decote > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">📉</div>
                            Décote
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            -
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.Decote, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                        </div>
                      )}

                      {ResultatTNS.state.DetailImpot.PrelevementsSociaux > 0 && (
                        <div className="card">
                          <div className="category-column">
                            <div className="titre-icone">🏥</div>
                            Prélèvements sociaux
                          </div>
                          <div className="value-column" style={{ textAlign: "center" }}>
                            {applyFormat(
                              ResultatTNS.state.DetailImpot.AssietteTotaleIR?.DividendeActivitePrelevementSociaux +
                                ResultatTNS.state.DetailImpot.AssietteTotaleIR?.RevenuSoumisPrelevementSociaux,
                              "uint",
                              { thousands: " ", decimal: ",", unit: " €" }
                            )}
                          </div>
                          <div className="value-column" style={{ textAlign: "right" }}>
                            {applyFormat(ResultatTNS.state.DetailImpot.PrelevementsSociaux, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                          </div>
                        </div>
                      )}

                      {/* Total avec style spécial - séparé avec espacement */}
                    </div>

                    {/* Conteneur séparé pour le total */}
                    <div className="card-list-container" style={{ marginTop: '16px' }}>
                      <div className="card" style={{ fontWeight: '600', background: 'linear-gradient(135deg, #8BB9FF 0%, #6DA3FF 100%)', color: 'white' }}>
                        <div className="category-column" style={{ fontWeight: '700', color: 'white' }}>
                          <div className="titre-icone" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>∑</div>
                          Total
                        </div>
                        <div className="value-column">
                          {/* Colonne vide pour l'alignement */}
                        </div>
                        <div className="value-column" style={{ textAlign: "right", fontWeight: '700', color: 'white' }}>
                          {applyFormat(ResultatTNS.state.DetailImpot.IRTotal, "uint", { thousands: " ", decimal: ",", unit: " €" })}
                        </div>
                      </div>
                    </div>
                  </div>
                </Row>
              </Column>
            </CardBox>
          </Column>
        </Row>
      </Column>
      <Modal
        width="80%"
        height="82%"
        title="Contexte fiscal"
        icon={<FaGlobe />}
        visible={modalOpen}
        onClose={() => {
          setmodalOpen(false);
        }}
      >
        <ContexteFiscalPageModal
          Outil=""
          visible={modalOpen}
          onClose={() => {
            setmodalOpen(false);
          }}
        />
      </Modal>
      {isUploadingIR && (
        <div
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "row",
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(15,10,30,0.45)",
            zIndex: 1000000,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              background: "linear-gradient(145deg, #ffffff, #f5f3ff)",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(139,92,246,0.25), 0 2px 8px rgba(0,0,0,0.12)",
              padding: "36px 40px",
              gap: "20px",
              minWidth: "340px",
              border: "1px solid rgba(139,92,246,0.15)",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 14px rgba(139,92,246,0.45)",
                animation: "pulse 1.8s ease-in-out infinite",
              }}
            >
              <FaFileInvoiceDollar style={{ color: "white", fontSize: "22px" }} />
            </div>
            <div style={{ textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: "15px", fontWeight: 600, fontFamily: "Inter, sans-serif", color: "#1e1b4b" }}>Analyse de l'avis d'imposition</p>
              <p style={{ margin: "4px 0 0", fontSize: "12px", fontFamily: "Inter, sans-serif", color: "#6b7280" }}>Extraction des données fiscales en cours...</p>
            </div>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  width: "100%",
                  height: "10px",
                  backgroundColor: "#ede9fe",
                  borderRadius: "999px",
                  overflow: "hidden",
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #a78bfa, #7c3aed)",
                    borderRadius: "999px",
                    transition: "width 0.3s ease-out",
                    boxShadow: "0 0 8px rgba(139,92,246,0.6)",
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "12px",
                  color: "#7c3aed",
                  fontWeight: 600,
                }}
              >
                <span>Progression</span>
                <span>{uploadProgress} %</span>
              </div>
            </div>
            <style>{`
              @keyframes pulse {
                0%, 100% { transform: scale(1); box-shadow: 0 4px 14px rgba(139,92,246,0.45); }
                50% { transform: scale(1.08); box-shadow: 0 6px 20px rgba(139,92,246,0.65); }
              }
            `}</style>
          </div>
        </div>
      )}
      <input ref={declarationIrInputRef} type="file" style={{ display: "none" }} onChange={handleDeclarationIrFileChange} />
      <Modal width="65%" height="72%" title="Vérification des données importées" icon={<FaFileInvoiceDollar />} visible={modalAvisImpotOpen} onClose={() => setModalAvisImpotOpen(false)}>
        {avisImpotData && (
          <Pager
            pages={[
              {
                content: (
                  <div className="revenuspro-container">
                    <Row bpad>
                      <InfoMessage
                        text={
                          context.state["PeriodeActuelle"][0]?.Statut === "TNS individuel"
                            ? "Les revenus professionnels ne sont pas importés depuis l'avis d'imposition car ils sont déjà renseignés dans la situation professionnelle."
                            : "Les revenus professionnels seront automatiquement affectés aux champs appropriés selon le régime fiscal détecté (micro-entreprise ou régime réel)."
                        }
                      />
                    </Row>
                    <div className="header-card">
                      <div className="category-column" style={{ flex: 2 }}>
                        <span className="header-icon">📄</span>
                        Autres revenus du foyer
                      </div>
                      <div className="value-column">
                        <span className="header-icon">👤</span>
                        Client
                      </div>
                      <div className="value-column">
                        <span className="header-icon">👥</span>
                        Conjoint
                      </div>
                    </div>
                    <div className="card-list-container">
                      <div className="card">
                        <div className="category-column" style={{ flex: 2 }}>
                          <div className="titre-icone">💰</div>
                          Traitements et salaires
                        </div>
                        <div className="value-column">
                          <TextField
                            value={avisImpotData.declarant1.traitement}
                            onChange={(e) => setAvisImpotData((prev) => ({ ...prev, declarant1: { ...prev.declarant1, traitement: e.target.value } }))}
                            dataType="uint"
                            format={{ thousands: " ", unit: " €" }}
                            normal
                          />
                        </div>
                        <div className="value-column">
                          <TextField
                            value={avisImpotData.declarant2.traitement}
                            onChange={(e) => setAvisImpotData((prev) => ({ ...prev, declarant2: { ...prev.declarant2, traitement: e.target.value } }))}
                            dataType="uint"
                            format={{ thousands: " ", unit: " €" }}
                            normal
                          />
                        </div>
                      </div>
                      <div className="card">
                        <div className="category-column" style={{ flex: 2 }}>
                          <div className="titre-icone">📊</div>
                          Autres revenus imposables
                        </div>
                        <div className="value-column">
                          <TextField
                            value={avisImpotData.declarant1.autresRevenus}
                            onChange={(e) => setAvisImpotData((prev) => ({ ...prev, declarant1: { ...prev.declarant1, autresRevenus: e.target.value } }))}
                            dataType="uint"
                            format={{ thousands: " ", unit: " €" }}
                            normal
                          />
                        </div>
                        <div className="value-column">
                          <TextField
                            value={avisImpotData.declarant2.autresRevenus}
                            onChange={(e) => setAvisImpotData((prev) => ({ ...prev, declarant2: { ...prev.declarant2, autresRevenus: e.target.value } }))}
                            dataType="uint"
                            format={{ thousands: " ", unit: " €" }}
                            normal
                          />
                        </div>
                      </div>
                      {context.state["PeriodeActuelle"][0]?.Statut !== "TNS individuel" &&
                        (avisImpotData.declarant1.revenus_pro_regime_special_nets > 0 || avisImpotData.declarant2.revenus_pro_regime_special_nets > 0) && (() => {
                          const getLabel = (declarant) => {
                            if (!declarant.revenus_pro_type) return null;
                            if (declarant.revenus_pro_type === "BNC") return "Revenus BNC";
                            if (declarant.revenus_pro_type === "BIC") return "Revenus BIC";
                            if (declarant.revenus_pro_type === "BA") return "Revenus BA";
                            return "Revenus professionnels";
                          };
                          const label = getLabel(avisImpotData.declarant1) || getLabel(avisImpotData.declarant2) || "Revenus professionnels";
                          return (
                            <div className="card">
                              <div className="category-column" style={{ flex: 2 }}>
                                <div className="titre-icone">💼</div>
                                {label}
                              </div>
                              <div className="value-column">
                                <TextField
                                  value={avisImpotData.declarant1.revenus_pro_regime_special_nets}
                                  onChange={(e) => setAvisImpotData((prev) => ({ ...prev, declarant1: { ...prev.declarant1, revenus_pro_regime_special_nets: e.target.value } }))}
                                  dataType="uint"
                                  format={{ thousands: " ", unit: " €" }}
                                  normal
                                />
                              </div>
                              <div className="value-column">
                                <TextField
                                  value={avisImpotData.declarant2.revenus_pro_regime_special_nets}
                                  onChange={(e) => setAvisImpotData((prev) => ({ ...prev, declarant2: { ...prev.declarant2, revenus_pro_regime_special_nets: e.target.value } }))}
                                  dataType="uint"
                                  format={{ thousands: " ", unit: " €" }}
                                  normal
                                />
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                    <div className="header-card" style={{ marginTop: "16px", background: "linear-gradient(135deg, #8BB9FF 0%, #6DA3FF 100%)" }}>
                      <div className="category-column">
                        <span className="header-icon">💳</span>
                        Données fiscales
                      </div>
                    </div>
                    <div className="card-list-container">
                      <div className="card">
                        <div className="category-column" style={{ flex: 2 }}>
                          <div className="titre-icone">✅</div>
                          Crédit d'impôt
                        </div>
                        <div className="value-column" style={{ gridColumn: "span 2" }}>
                          <TextField
                            value={avisImpotData.creditImpot}
                            onChange={(e) => setAvisImpotData((prev) => ({ ...prev, creditImpot: e.target.value }))}
                            dataType="uint"
                            format={{ thousands: " ", unit: " €" }}
                            normal
                          />
                        </div>
                        <div className="value-column" />
                      </div>
                      <div className="card">
                        <div className="category-column" style={{ flex: 2 }}>
                          <div className="titre-icone">📉</div>
                          Déductions fiscales
                        </div>
                        <div className="value-column" style={{ gridColumn: "span 2" }}>
                          <TextField
                            value={avisImpotData.deductionFiscale}
                            onChange={(e) => setAvisImpotData((prev) => ({ ...prev, deductionFiscale: e.target.value }))}
                            dataType="uint"
                            format={{ thousands: " ", unit: " €" }}
                            normal
                          />
                        </div>
                        <div className="value-column" />
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
            page={0}
            navButtons={{
              cancel: { onClick: () => setModalAvisImpotOpen(false) },
              validate: { onClick: handleConfirmAvisImpot },
            }}
          />
        )}
      </Modal>
      <Modal
        width="80%"
        height="40%"
        title="Pack Expert"
        visible={modalPackExpert}
        onClose={() => setModalPackExpert(false)}
      >
        <Column fill>
          <Row fill>
            <Column fill center className="modal-content">
              <br />
              L'import de l'avis d'imposition est une fonctionnalité réservée aux détenteurs du <strong>Pack Expert</strong>
              <br />
              <br />
              Pour y souscrire, contactez-nous à l'adresse suivante : <a href="mailto:support@mako-remexpert.com"><strong>support@mako-remexpert.com</strong></a>
              <br />
              Nous revenons vers vous très rapidement !
            </Column>
          </Row>
        </Column>
      </Modal>
    </>
  );
};

export default ContexteFiscalePage;
